import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Layers, ShoppingBag, FileText, Users, Building2, BookOpen, Wallet,
  BarChart3, Settings as SettingsIcon, Search, Plus, LogOut, ShoppingCart, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/lib/settings";
import api, { apiError } from "@/lib/api";
import Modal from "@/components/Modal";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/accounts", label: "Accounts", icon: BookOpen, ownerOnly: true },
  { group: "Sell" },
  { to: "/sales/new", label: "New Sale", icon: ShoppingCart },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/customers", label: "Customers", icon: Users },
  { group: "Buy & stock" },
  { to: "/inventory", label: "Inventory", icon: Layers },
  { to: "/purchases", label: "Purchases", icon: ShoppingBag },
  { to: "/vendors", label: "Vendors", icon: Building2 },
  { group: "Money" },
  { to: "/expenses", label: "Expenses", icon: Wallet },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: SettingsIcon, ownerOnly: true },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const boxRef = useRef();

  useEffect(() => {
    if (!q || q.length < 2) {
      setRes(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const [sk, inv, cust] = await Promise.all([
          api.get(`/skus/search?q=${encodeURIComponent(q)}`),
          api.get(`/invoices?q=${encodeURIComponent(q)}`),
          api.get(`/customers?q=${encodeURIComponent(q)}`),
        ]);
        setRes({ skus: sk.data.slice(0, 5), invoices: inv.data.slice(0, 5), customers: cust.data.slice(0, 5) });
      } catch {
        setRes(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    setQ("");
    setRes(null);
    nav(path);
  };

  return (
    <div className="relative flex-1 max-w-md" ref={boxRef}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C929B]" />
      <input
        data-testid="global-search"
        className="field pl-9 py-2 rounded-full"
        placeholder="Search styles, invoices, customers…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {res && (
        <div className="absolute top-11 left-0 right-0 card shadow-xl z-50 p-2 max-h-96 overflow-auto" data-testid="search-results">
          {res.skus.length > 0 && <div className="eyebrow px-2 pt-2 pb-1">Inventory</div>}
          {res.skus.map((s) => (
            <button key={s.id} className="w-full text-left px-3 py-2 hover:bg-[#F4F0EA] rounded-lg text-sm flex justify-between" onClick={() => go(`/inventory?style=${s.style_id}`)}>
              <span>{s.style_name} · {s.color}/{s.size}</span>
              <span className="font-num text-[#8C929B]">{s.current_qty} pcs</span>
            </button>
          ))}
          {res.invoices.length > 0 && <div className="eyebrow px-2 pt-2 pb-1">Invoices</div>}
          {res.invoices.map((i) => (
            <button key={i.id} className="w-full text-left px-3 py-2 hover:bg-[#F4F0EA] rounded-lg text-sm flex justify-between" onClick={() => go(`/invoices/${i.id}`)}>
              <span className="font-num">{i.invoice_number}</span>
              <span className="text-[#8C929B]">{i.customer_name}</span>
            </button>
          ))}
          {res.customers.length > 0 && <div className="eyebrow px-2 pt-2 pb-1">Customers</div>}
          {res.customers.map((c) => (
            <button key={c.id} className="w-full text-left px-3 py-2 hover:bg-[#F4F0EA] rounded-lg text-sm" onClick={() => go(`/customers?id=${c.id}`)}>
              {c.name} <span className="text-[#8C929B]">· {c.phone}</span>
            </button>
          ))}
          {!res.skus.length && !res.invoices.length && !res.customers.length && (
            <div className="px-3 py-4 text-sm text-[#8C929B]">No matches for "{q}"</div>
          )}
        </div>
      )}
    </div>
  );
}

function ChangePassword({ open, onClose }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  useEffect(() => { if (open) setF({ current_password: "", new_password: "", confirm: "" }); }, [open]);
  const mismatch = f.confirm && f.confirm !== f.new_password;
  const submit = async () => {
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed");
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Change password" testid="change-password">
      <div className="space-y-4">
        <div><label className="label">Current password</label><input type="password" className="field" autoComplete="current-password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></div>
        <div><label className="label">New password</label><input type="password" className="field" autoComplete="new-password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
          <div className="text-xs text-[#8C929B] mt-1">At least 8 characters</div></div>
        <div><label className="label">Confirm new password</label><input type="password" className={`field ${mismatch ? "border-[#A3411D]" : ""}`} autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} /></div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!f.current_password || f.new_password.length < 8 || f.new_password !== f.confirm}>Change password</button>
        </div>
      </div>
    </Modal>
  );
}

export default function Layout() {
  const { user, logout, isOwner } = useAuth();
  const nav = useNavigate();
  const settings = useSettings();
  const [pwOpen, setPwOpen] = useState(false);
  const brand = settings?.company_name || "Arul Mozhi Store";
  const initials = brand.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-[#E6E1D8] bg-[#FBF9F5] flex flex-col fixed h-screen no-print">
        <div className="px-6 py-6 border-b border-[#E6E1D8]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 shrink-0 rounded-full bg-[#0E1B2E] text-white grid place-items-center font-display text-lg">{initials}</div>
            <div className="min-w-0">
              <div className="font-display text-lg leading-tight truncate">{brand}</div>
              <div className="eyebrow mt-1">Back-Office</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-auto">
          {NAV.filter((n) => !n.ownerOnly || isOwner).map((n) => n.group ? (
            <div key={n.group} className="eyebrow px-3 pt-4 pb-1">{n.group}</div>
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0E1B2E] text-white" : "text-[#5C626A] hover:bg-[#F4F0EA]"
                }`
              }
            >
              <n.icon size={18} strokeWidth={1.6} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E6E1D8]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`chip border ${isOwner ? "bg-[#0E1B2E] text-white border-[#0E1B2E]" : "bg-[#FEF6E6] text-[#B38E46] border-[#EAD096]"}`}>
                {isOwner ? "Owner" : "Staff"}
              </span>
            </div>
          </div>
          <div className="flex gap-1.5">
            <button className="btn btn-ghost btn-sm flex-1 justify-center" onClick={() => setPwOpen(true)} data-testid="change-password-btn" title="Change password">
              <KeyRound size={15} />
            </button>
            <button className="btn btn-ghost btn-sm flex-[3] justify-center" onClick={logout} data-testid="logout-btn">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </aside>
      <ChangePassword open={pwOpen} onClose={() => setPwOpen(false)} />

      {/* Main */}
      <div className="flex-1 ml-60 no-print-ml">
        <header className="sticky top-0 z-40 bg-[#FBF9F5]/90 backdrop-blur-md border-b border-[#E6E1D8] px-8 py-3 flex items-center gap-4 no-print">
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            {settings?.gstin
              ? <span className="chip border bg-white border-[#E6E1D8] text-[#5C626A] font-num">GSTIN {settings.gstin}</span>
              : isOwner && <button className="chip border bg-[#FEF6E6] border-[#EAD096] text-[#B38E46]" onClick={() => nav("/settings")}>Add GSTIN in Settings</button>}
            <button className="btn btn-ghost btn-sm" onClick={() => nav("/expenses?new=1")} data-testid="header-new-expense">
              <Plus size={15} /> Expense
            </button>
            {isOwner && (
              <button className="btn btn-gold btn-sm" onClick={() => nav("/purchases?new=1")} data-testid="header-new-purchase">
                <Plus size={15} /> Purchase
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => nav("/sales/new")} data-testid="header-new-sale">
              <Plus size={15} /> New Invoice
            </button>
          </div>
        </header>
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
